import React from 'react';
import Box from '@mui/material/Box';
import { Palette, useTheme } from '@mui/material/styles';
import { BoxProps } from '@mui/material/Box';
import { FFTheme } from '../../assets/theme';

type OwnerState = {
  variant?: string;
  bgColor?: string;
  color?: string;
  opacity?: number;
  borderRadius?: string;
  shadow?: string;
  coloredShadow?: string;
};

type MDBoxRootProps = BoxProps & {
  ownerState: OwnerState;
};

type KeyPalette = keyof Palette;

const MDBoxRoot: React.FC<MDBoxRootProps> = ({ ownerState, ...rest }) => {
  const theme = useTheme<FFTheme>();
  const { palette, functions, borders, boxShadows } = theme;
  const {
    variant,
    bgColor,
    color,
    opacity,
    borderRadius,
    shadow,
    coloredShadow,
  } = ownerState;

  const { gradients, grey, white } = palette as any;
  const { linearGradient } = functions;
  const { borderRadius: radius } = borders;
  const { colored } = boxShadows;

  const greyColors = {
    'grey-100': grey[100],
    'grey-200': grey[200],
    'grey-300': grey[300],
    'grey-400': grey[400],
    'grey-500': grey[500],
    'grey-600': grey[600],
    'grey-700': grey[800],
    'grey-800': grey[800],
    'grey-900': grey[900],
  };

  const validGradients = [
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
    'dark',
    'light',
  ];

  const validColors = [
    'transparent',
    'white',
    'black',
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
    'light',
    'dark',
    'text',
    'grey-100',
    'grey-200',
    'grey-300',
    'grey-400',
    'grey-500',
    'grey-600',
    'grey-700',
    'grey-800',
    'grey-900',
  ];

  const validBorderRadius = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'section'];
  const validBoxShadows = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'inset'];

  let backgroundValue = bgColor;

  if (variant === 'gradient') {
    backgroundValue =
      bgColor && validGradients.find((el) => el === bgColor)
        ? linearGradient(gradients[bgColor].main, gradients[bgColor].state)
        : white.main;
  } else if (validColors.find((el) => el === bgColor)) {
    backgroundValue =
      bgColor && palette[bgColor as KeyPalette]
        ? (palette[bgColor as KeyPalette] as any).main
        : (greyColors[bgColor as keyof typeof greyColors] as string);
  } else {
    backgroundValue = bgColor;
  }

  let colorValue = color;

  if (validColors.find((el) => el === color)) {
    colorValue = (palette[color as KeyPalette] as any)
      ? (palette[color as KeyPalette] as any).main
      : greyColors[color as keyof typeof greyColors];
  }

  let borderRadiusValue = borderRadius;

  if (validBorderRadius.find((el) => el === borderRadius)) {
    borderRadiusValue = radius[borderRadius as keyof typeof borderRadius];
  }

  let boxShadowValue: any = 'none';

  if (validBoxShadows.find((el) => el === shadow)) {
    boxShadowValue = boxShadows[shadow as keyof typeof boxShadows];
  } else if (coloredShadow) {
    boxShadowValue = colored[coloredShadow as keyof typeof colored]
      ? colored[coloredShadow as keyof typeof colored]
      : 'none';
  }

  return (
    <Box
      {...rest}
      sx={{
        opacity,
        background: backgroundValue,
        color: colorValue,
        borderRadius: borderRadiusValue,
        boxShadow: boxShadowValue,
        ...(rest && rest.sx || {}),
      }}
    />
  );
};

export default MDBoxRoot;
